<template>
  <div class="admin-container">
    <!-- 侧边栏 -->
    <div class="sidebar">
      <div class="logo-section">
        <img src="@/assets/logo.png" alt="Logo" class="logo" />
        <h2>管理后台</h2>
      </div>
      <a-menu
        v-model:selectedKeys="selectedKeys"
        theme="dark"
        mode="inline"
      >
        <a-menu-item key="dashboard">
          <template #icon><dashboard-outlined /></template>
          <span>公告管理</span>
        </a-menu-item>
        <a-menu-item key="upload">
          <template #icon><upload-outlined /></template>
          <span>图片上传</span>
        </a-menu-item>
        <a-menu-item key="manage">
          <template #icon><picture-outlined /></template>
          <span>图片管理</span>
        </a-menu-item>
      </a-menu>
      <div class="logout-section">
        <a-button type="primary" danger @click="handleLogout">
          <template #icon><logout-outlined /></template>
          退出登录
        </a-button>
      </div>
    </div>

    <!-- 主要内容区 -->
    <div class="main-content">
      <!-- 顶部栏 -->
      <div class="header">
        <h1>{{ pageTitle }}</h1>
        <div class="user-info">
          <span>管理员</span>
          <a-avatar>
            <template #icon><user-outlined /></template>
          </a-avatar>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="content">
        <!-- 图片上传区域 -->
        <div class="upload-section" v-if="selectedKeys[0] === 'upload'">
          <a-card title="图片上传" :bordered="false">
            <!-- 封面图片上传 -->
            <div class="cover-upload">
              <h3>封面图片</h3>
              <div class="cover-area" v-if="!coverPreview">
                <a-upload
                  :show-upload-list="false"
                  :before-upload="handleCoverUpload"
                >
                  <div class="cover-placeholder">
                    <plus-outlined />
                    <p>点击上传封面图片</p>
                  </div>
                </a-upload>
              </div>
              <div class="cover-preview" v-else>
                <img :src="coverPreview" alt="封面预览" />
                <div class="cover-actions">
                  <a-button type="primary" danger size="small" @click="removeCover">
                    <template #icon><delete-outlined /></template>
                  </a-button>
                </div>
              </div>
            </div>

            <a-divider />

            <!-- 原有的图片上传区域 -->
            <div class="upload-area">
              <a-upload-dragger
                v-model:fileList="fileList"
                name="file"
                :multiple="true"
                :before-upload="handleContentBeforeUpload"
                @change="handleChange"
                :auto-upload="false"
              >
                <p class="ant-upload-drag-icon">
                  <inbox-outlined />
                </p>
                <p class="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p class="ant-upload-hint">
                  支持单个或批量上传，严禁上传违规图片
                </p>
              </a-upload-dragger>
            </div>

            <div class="upload-options">
              <a-form layout="vertical">
                <a-form-item label="图片分类">
                  <a-select v-model:value="category">
                    <a-select-option value="asian-male">亚洲男</a-select-option>
                    <a-select-option value="asian-female">亚洲女</a-select-option>
                    <a-select-option value="european-male">欧洲男</a-select-option>
                    <a-select-option value="european-female">欧洲女</a-select-option>
                  </a-select>
                </a-form-item>
                <a-form-item label="图片描述">
                  <a-textarea v-model:value="description" rows="4" placeholder="请输入图片描述" />
                </a-form-item>
                <a-form-item>
                  <div class="submit-section">
                    <a-button 
                      type="primary" 
                      :loading="uploading" 
                      :disabled="!canSubmit"
                      @click="handleSubmit"
                    >
                      确认上传
                    </a-button>
                    <div class="upload-tip" v-if="!canSubmit">
                      <info-circle-outlined />
                      <span>请上传封面图片和至少一张内容图片</span>
                    </div>
                  </div>
                </a-form-item>
              </a-form>
            </div>
          </a-card>
        </div>

        <!-- 图片管理区域 -->
        <div class="manage-section" v-if="selectedKeys[0] === 'manage'">
          <a-card title="图片管理" :bordered="false">
            <template #extra>
              <a-spin v-if="loading" />
            </template>
            <div class="filter-section">
              <a-space>
                <a-select v-model:value="filterCategory" style="width: 200px" placeholder="选择分类">
                  <a-select-option value="">全部分类</a-select-option>
                  <a-select-option value="asian-male">亚洲男</a-select-option>
                  <a-select-option value="asian-female">亚洲女</a-select-option>
                  <a-select-option value="european-male">欧洲男</a-select-option>
                  <a-select-option value="european-female">欧洲女</a-select-option>
                </a-select>
                <a-input-search
                  v-model:value="searchText"
                  placeholder="搜索图片描述"
                  style="width: 300px"
                  @search="onSearch"
                />
              </a-space>
            </div>

            <div class="image-grid">
              <template v-if="!loading">
                <a-empty v-if="filteredImages.length === 0" />
                <a-card v-else v-for="(image, index) in filteredImages" 
                        :key="index" 
                        hoverable 
                        class="image-card">
                  <template #cover>
                    <img :src="getFullUrl(image.url)" :alt="image.description" />
                  </template>
                  <template #actions>
                    <delete-outlined key="delete" @click="handleDelete(image)" />
                  </template>
                  <a-card-meta 
                    :title="getCategoryName(image.category)"
                    :description="image.description" />
                </a-card>
              </template>
              <div v-else class="loading-container">
                <a-spin size="large" />
              </div>
            </div>

            <div class="pagination">
              <a-pagination
                v-model:current="current"
                :total="total"
                show-size-changer
                show-quick-jumper
              />
            </div>
          </a-card>
        </div>

        <!-- 公告管理部分 -->
        <div class="dashboard-section" v-if="selectedKeys[0] === 'dashboard'">
          <a-card title="公告管理" :bordered="false">
            <template #extra>
              <a-space>
                <a-button type="primary" :loading="saving" @click="saveNotice">
                  保存修改
                </a-button>
              </a-space>
            </template>

            <a-form layout="vertical">
              <a-form-item label="公告标题">
                <a-input v-model:value="notice.title" placeholder="请输入公告标题" />
              </a-form-item>
              <a-form-item label="公告内容">
                <a-textarea
                  v-model:value="notice.content"
                  :rows="10"
                  placeholder="请输入公告内容，支持换行"
                />
              </a-form-item>
            </a-form>

            <div class="preview-section">
              <h3>预览效果</h3>
              <div class="preview-content">
                <h2>{{ notice.title }}</h2>
                <div class="preview-text">
                  <p v-for="(line, index) in noticeContentLines" :key="index">
                    {{ line }}
                  </p>
                </div>
              </div>
            </div>
          </a-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { useRouter } from 'vue-router'
import {
  DashboardOutlined,
  UploadOutlined,
  PictureOutlined,
  LogoutOutlined,
  UserOutlined,
  InboxOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined
} from '@ant-design/icons-vue'
import { getApiUrl, getResourceUrl } from '@/utils/api'

export default defineComponent({
  name: 'AdminView',
  components: {
    DashboardOutlined,
    UploadOutlined,
    PictureOutlined,
    LogoutOutlined,
    UserOutlined,
    InboxOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    InfoCircleOutlined
  },
  setup() {
    const router = useRouter()
    const selectedKeys = ref(['upload'])
    const fileList = ref([])
    const category = ref('asian-male')
    const description = ref('')
    const filterCategory = ref('')
    const searchText = ref('')
    const current = ref(1)
    const total = ref(0)
    const coverFile = ref(null)
    const coverPreview = ref('')
    const uploading = ref(false)
    const images = ref([])
    const loading = ref(false)

    // 公告相关状态
    const notice = ref({
      title: '',
      content: ''
    })
    const saving = ref(false)

    // 获取图片数据
    const fetchImages = async () => {
      try {
        loading.value = true
        const categories = ['asian-male', 'asian-female', 'european-male', 'european-female']
        const allImages = []

        for (const cat of categories) {
          const response = await fetch(getApiUrl(`/api/photos/${cat}`))
          if (!response.ok) {
            throw new Error(`获取${cat}分类图片失败`)
          }
          const data = await response.json()
          // 为每个图片添加分类信息
          const processedData = data.map(item => ({
            ...item,
            category: cat,
            url: item.coverUrl
          }))
          allImages.push(...processedData)
        }

        images.value = allImages
        total.value = allImages.length
      } catch (error) {
        message.error('获取图片失败：' + error.message)
      } finally {
        loading.value = false
      }
    }

    // 获取公告内容
    const fetchNotice = async () => {
      try {
        const response = await fetch(getApiUrl('/api/notice'), {
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        })
        if (!response.ok) {
          throw new Error('获取公告失败')
        }
        const data = await response.json()
        notice.value = data
      } catch (error) {
        message.error('获取公告失败：' + error.message)
        console.error('获取公告错误:', error)
      }
    }

    // 监听选中的菜单项变化
    watch(selectedKeys, (newValue) => {
      if (newValue[0] === 'manage') {
        fetchImages()
      }
      if (newValue[0] === 'dashboard') {
        fetchNotice()
      }
    })

    const pageTitle = computed(() => {
      const titles = {
        dashboard: '控制台',
        upload: '图片上传',
        manage: '图片管理'
      }
      return titles[selectedKeys.value[0]] || '控制台'
    })

    const filteredImages = computed(() => {
      return images.value.filter(image => {
        if (filterCategory.value && image.category !== filterCategory.value) {
          return false
        }
        if (searchText.value && !image.description.toLowerCase().includes(searchText.value.toLowerCase())) {
          return false
        }
        return true
      })
    })

    const canSubmit = computed(() => {
      return coverFile.value && fileList.value.length > 0 && category.value && description.value
    })

    const handleCoverUpload = (file) => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        message.error('只能上传图片文件！')
        return false
      }

      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        message.error('图片大小不能超过 5MB！')
        return false
      }

      coverFile.value = file
      coverPreview.value = URL.createObjectURL(file)
      return false // 阻止自动上传
    }

    const removeCover = () => {
      coverFile.value = null
      coverPreview.value = ''
    }

    const handleContentBeforeUpload = (file) => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        message.error('只能上传图片文件！')
        return false
      }

      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        message.error('图片大小不能超过 5MB！')
        return false
      }

      // 检查是否已经存在相同的文件
      const isDuplicate = fileList.value.some(item => 
        item.name === file.name || item.uid === file.uid
      )
      if (isDuplicate) {
        message.warning('已存在相同的文件')
        return false
      }

      return false // 阻止自动上传
    }

    const handleChange = ({ fileList: newFileList }) => {
      // 过滤掉重复的文件
      const uniqueFiles = []
      const seen = new Set()
      
      newFileList.forEach(file => {
        if (!seen.has(file.uid)) {
          seen.add(file.uid)
          uniqueFiles.push({
            ...file,
            status: 'done',
            url: file.url || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : undefined)
          })
        }
      })
      
      fileList.value = uniqueFiles
    }

    const handleDrop = (e) => {
      console.log('Dropped files', e.dataTransfer.files)
    }

    const handleEdit = (image) => {
      console.log('Edit image', image)
    }

    const handleDelete = async (image) => {
      try {
        Modal.confirm({
          title: '确认删除',
          content: '确定要删除这套图片吗？此操作不可恢复。',
          okText: '确认',
          cancelText: '取消',
          okType: 'danger',
          async onOk() {
            try {
              const response = await fetch(getApiUrl(`/api/photoset/${image.id}`), {
                method: 'DELETE'
              });

              if (!response.ok) {
                throw new Error('删除失败');
              }

              message.success('删除成功');
              // 重新获取图片列表
              fetchImages();
            } catch (error) {
              message.error('删除失败：' + error.message);
            }
          }
        });
      } catch (error) {
        message.error('操作失败：' + error.message);
      }
    }

    const handleLogout = () => {
      router.push('/main')
    }

    const onSearch = (value) => {
      console.log('Search:', value)
    }

    const handleSubmit = async () => {
      if (!canSubmit.value) {
        message.warning('请确保已上传封面图片和至少一张内容图片，并填写完整信息')
        return
      }

      try {
        uploading.value = true
        const formData = new FormData()
        
        // 添加基本信息
        formData.append('title', description.value)
        formData.append('description', description.value)
        formData.append('category', category.value)
        
        // 添加封面图片
        formData.append('cover', coverFile.value)
        
        // 添加内容图片
        fileList.value.forEach((file, index) => {
          if (file.originFileObj) {
            formData.append(`photos`, file.originFileObj)
          }
        })

        console.log('上传的文件数量:', fileList.value.length)
        console.log('FormData中的文件:', formData.getAll('photos').length)

        // 发送上传请求
        const response = await fetch(getApiUrl('/api/photoset'), {
          method: 'POST',
          body: formData
        })

        if (!response.ok) {
          throw new Error('上传失败')
        }

        const result = await response.json()
        message.success('上传成功！')
        
        // 重置表单
        coverFile.value = null
        coverPreview.value = ''
        fileList.value = []
        description.value = ''
        category.value = undefined
        
      } catch (error) {
        message.error('上传失败：' + error.message)
      } finally {
        uploading.value = false
      }
    }

    const getCategoryName = (category) => {
      const categoryNames = {
        'asian-male': '亚洲男',
        'asian-female': '亚洲女',
        'european-male': '欧洲男',
        'european-female': '欧洲女'
      }
      return categoryNames[category] || category
    }

    // 保存公告
    const saveNotice = async () => {
      try {
        saving.value = true
        const response = await fetch(getApiUrl('/api/notice'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            title: notice.value.title,
            content: notice.value.content
          })
        })

        if (!response.ok) {
          const errorData = await response.text()
          throw new Error(errorData || '保存失败')
        }

        message.success('保存成功')
      } catch (error) {
        message.error('保存失败：' + error.message)
        console.error('保存公告错误:', error)
      } finally {
        saving.value = false
      }
    }

    // 计算公告内容的行数组
    const noticeContentLines = computed(() => {
      return notice.value.content.split('\n')
    })

    // 处理URL
    const getFullUrl = (url) => {
      return getResourceUrl(url)
    }

    return {
      selectedKeys,
      fileList,
      category,
      description,
      filterCategory,
      searchText,
      current,
      total,
      images,
      filteredImages,
      pageTitle,
      coverPreview,
      uploading,
      loading,
      canSubmit,
      handleCoverUpload,
      removeCover,
      handleContentBeforeUpload,
      handleChange,
      handleDrop,
      handleEdit,
      handleDelete,
      handleLogout,
      onSearch,
      handleSubmit,
      getCategoryName,
      notice,
      saving,
      saveNotice,
      noticeContentLines,
      getFullUrl
    }
  }
})
</script>

<style scoped>
.admin-container {
  display: flex;
  min-height: 100vh;
  background: #f0f2f5;
}

.sidebar {
  width: 256px;
  background: #001529;
  display: flex;
  flex-direction: column;
}

.logo-section {
  padding: 24px;
  text-align: center;
}

.logo {
  width: 40px;
  height: 40px;
  margin-bottom: 12px;
}

.logo-section h2 {
  color: #fff;
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.logout-section {
  margin-top: auto;
  padding: 24px;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  background: #fff;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
}

.header h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.content {
  padding: 24px;
  overflow: auto;
}

.upload-section, .manage-section {
  max-width: 1200px;
  margin: 0 auto;
}

.upload-area {
  margin-bottom: 24px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  margin: 24px 0;
}

.image-card {
  width: 100%;
}

.image-card :deep(img) {
  height: 200px;
  object-fit: cover;
}

.filter-section {
  margin-bottom: 24px;
}

.pagination {
  text-align: right;
  margin-top: 24px;
}

:deep(.ant-upload-drag) {
  background: #fafafa;
  border: 2px dashed #d9d9d9;
}

:deep(.ant-upload-drag:hover) {
  border-color: #1890ff;
}

.cover-upload {
  margin-bottom: 24px;
}

.cover-upload h3 {
  margin-bottom: 16px;
  color: #333;
  font-size: 16px;
}

.cover-area {
  width: 200px;
  height: 200px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.3s;
}

.cover-area:hover {
  border-color: #1890ff;
}

.cover-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
}

.cover-placeholder .anticon {
  font-size: 24px;
  margin-bottom: 8px;
}

.cover-preview {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
}

.cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 4px;
  padding: 4px;
}

.submit-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.upload-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #999;
}

.upload-tip .anticon {
  color: #1890ff;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}

.preview-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.preview-section h3 {
  margin-bottom: 16px;
  color: #1890ff;
}

.preview-content {
  padding: 24px;
  background: #f8f8f8;
  border-radius: 8px;
}

.preview-content h2 {
  text-align: center;
  color: #1890ff;
  margin-bottom: 20px;
}

.preview-text p {
  margin-bottom: 12px;
  line-height: 1.6;
  color: #333;
}
</style> 